import React, { useEffect, useRef, useState } from "react";
import { Typography, Button, Card, Row, Col, Space, Empty } from "antd";
import { Doughnut, getElementAtEvent } from "react-chartjs-2";
import { useSelector } from "react-redux";
import arrowIcon from 'assets/arrow.png';
import jsPDF from "jspdf"
import { generateStagesPDF } from "../utils/generateStagesPDF";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

// ✅ Only register necessary components
ChartJS.register(ArcElement, Tooltip, Legend);

const { Text } = Typography;

const Stages = ({ selectedFilter, isExporting, stagesDataRef, stagesRef, setIndividualExportLoading, setExportMessage }) => {
  const chartRef = useRef(null);
  const corporateData = useSelector((state) => state?.auth?.corporateData)
  const [selectedStage, setSelectedStage] = useState("Under Evaluation"); // Set "Under Evaluation" as default

  // Get stages chart data from Redux store
  const stagesChartData = useSelector(state => state?.corporateDashboardPage2?.stagesChartData);

  const getStageColors = () => {
    return {
      "Under Evaluation": "#002B5B",
      "Offered": "#00509E",
      "Joined": "#0078D7",
      "Left": "#7EC8E3"
    };
  };

  const getGrayColors = () => {
    return {
      "Under Evaluation": "#D9D9D9",
      "Offered": "#D9D9D9",
      "Joined": "#D9D9D9",
      "Left": "#D9D9D9"
    };
  };

  const colors = getStageColors();
  const grayColors = getGrayColors();

  // Check if there's any data
  const hasData = stagesChartData && (
    (stagesChartData.scheduledCount || 0) > 0 ||
    (stagesChartData.offeredCount || 0) > 0 ||
    (stagesChartData.joinedCount || 0) > 0 ||
    (stagesChartData.leftCount || 0) > 0
  );

  console.log('Stages hasData:', hasData, 'stagesChartData:', stagesChartData);

  // Prepare chart data from API response
  const getChartData = () => {
    const stageLabels = ["Under Evaluation", "Offered", "Joined", "Left"];

    if (!hasData) {
      // Show full gray circle when no data (like GenderDonutChart)
      return {
        labels: [''],
        datasets: [{
          data: [100],
          backgroundColor: ['#d9d9d9'],
          borderColor: '#FFFFFF',
          borderWidth: 0,
          cutout: "65%",
        }],
      };
    }

    return {
      labels: stageLabels,
      datasets: [{
        data: [
          stagesChartData?.scheduledCount || 0,
          stagesChartData?.offeredCount || 0,
          stagesChartData?.joinedCount || 0,
          stagesChartData?.leftCount || 0
        ],
        backgroundColor: Object.values(colors),
        borderColor: '#FFFFFF',
        borderWidth: 0,
        cutout: "65%",
      }],
    };
  };

  const data = getChartData();

  // Export handler
  const handleExport = async () => {
    try {
      setIndividualExportLoading(true);
      setExportMessage('Exporting Stages...');
      const pdf = new jsPDF("p", "mm", "a4")

      await generateStagesPDF({
        pdf,
        corporateData,
        stagesRef: stagesRef,
        stagesData: stagesDataRef?.current
      })

      pdf.save("Stages.pdf")
    } finally {
      setIndividualExportLoading(false);
    }
  }

  // Get value for a specific stage
  const getStageValue = (stage) => {
    if (!stagesChartData || !stage) return 0;
    switch (stage) {
      case "Under Evaluation": return stagesChartData.scheduledCount || 0;
      case "Offered": return stagesChartData.offeredCount || 0;
      case "Joined": return stagesChartData.joinedCount || 0;
      case "Left": return stagesChartData.leftCount || 0;
      default: return 0;
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context) => {
            if (!hasData) {
              return `: 0`;
            }
            return `${context.label}: ${context.parsed.toLocaleString()}`;
          }
        }
      },
      datalabels: { display: false },
    },
    devicePixelRatio: window.devicePixelRatio || 2,
  };

  // ✅ Handle chart click
  const handleChartClick = (event) => {
    const chart = chartRef.current;
    if (!chart) return;

    const elements = getElementAtEvent(chart, event);
    if (elements.length > 0) {
      const datasetIndex = elements[0].datasetIndex;
      const index = elements[0].index;
      const label = data.labels[index];
      const value = data.datasets[datasetIndex].data[index];
      console.log(`🟢 Clicked on chart segment: ${label} - ${value} candidates`);
      // You can replace this with highlight logic or API call
    }
  };

  // ✅ Handle label click
  const handleLabelClick = (label, value) => {
    console.log(`🟦 Clicked on label: ${label} - ${value} candidates`);
    setSelectedStage(label); // Update selected stage on label click
    // Perform logic here (highlight, filter, navigate, etc.)
  };

  useEffect(() => {
    const handleResize = () => window.dispatchEvent(new Event("resize"));
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Populate stagesDataRef with current stages data for export
  useEffect(() => {
    if (stagesDataRef && stagesChartData) {
      const stagesData = [
        { stage: "Under Evaluation", count: stagesChartData.scheduledCount || 0 },
        { stage: "Offered", count: stagesChartData.offeredCount || 0 },
        { stage: "Joined", count: stagesChartData.joinedCount || 0 },
        { stage: "Left", count: stagesChartData.leftCount || 0 },
      ];
      stagesDataRef.current = stagesData;
    }
  }, [stagesChartData, stagesDataRef]);

  return (
    <Card
      style={{
        borderRadius: 16,
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        padding: "24px 28px",
        background: "#fff",
        position: "relative",
      }}
    >
      {/* === Export Button at Top Right === */}
      {!isExporting && (
        <div
          className="no-export"
          style={{
            position: "absolute",
            top: 20,
            right: 28,
          }}
        >
          <Button
            type="default"
            size="middle"
            onClick={handleExport}
            disabled={!hasData}
            style={{
              borderRadius: "4px",
              borderColor: "#0FB680",
              color: "#0FB680",
              fontWeight: 500,
            }}
          >
            Export
          </Button>
        </div>
      )}

      <div>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text strong style={{ fontSize: "20px" }}>
            Stages
          </Text>
        </div>

        {/* Chart + List */}
        <div
          ref={stagesRef}
          style={{
            display: "flex",
            justifyContent: "flex-start",
            marginTop: 20
          }}
        >
          {hasData ? (
            <div
              className="stages-chart-export"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "20px"
              }}
            >
              {/* Crisp Donut Chart with onClick */}
              <div
                style={{
                  width: 280,
                  height: 280,
                  position: "relative",
                  cursor: "pointer",
                }}
                onClick={handleChartClick}
              >
                <Doughnut ref={chartRef} data={data} options={options} />
              </div>

              {/* Label + Value stacked layout */}
              <div style={{ marginLeft: 20 }}>
                {data.labels.map((label, i) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      width: "200px",
                      marginBottom: 14,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => handleLabelClick(label, data.datasets[0].data[i])}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = 0.7)}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = 1)}
                  >
                    {/* Dot + text */}
                    <div style={{ display: "flex", alignItems: "flex-start" }}>
                      <span
                        style={{
                          display: "inline-block",
                          width: 10,
                          height: 10,
                          backgroundColor: colors[label],
                          borderRadius: "50%",
                          marginRight: 10,
                          marginTop: 5,
                        }}
                      />

                      {/* City name + value stacked vertically */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          lineHeight: 1.2,
                        }}
                      >
                        <Text
                          strong
                          style={{
                            color: selectedStage === label ? "rgba(50, 73, 215, 1)" : "#000",
                            fontSize: "15px",
                          }}
                        >
                          {label}
                        </Text>
                        <Text
                          strong
                          style={{
                            fontSize: "15px",
                            color: selectedStage === label ? "rgba(50, 73, 215, 1)" : "#000",
                          }}
                        >
                          {getStageValue(label)}
                        </Text>
                      </div>
                    </div>

                    {/* Arrow */}
                    <img
                      src={arrowIcon}
                      alt="arrow"
                      style={{
                        marginTop: 6,
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              className="stages-chart-export"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "30px"
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                width: 280,
                height: 280,
                position: "relative",
                marginLeft: "40px"
              }}>
                <Empty />
              </div>
            </div>
          )}
        </div>

        {/* Bottom Info Box (clickable) */}
        {!isExporting && hasData && (
          <div
            className="no-export"
            style={{
              width: '50%',
              borderRadius: '8px',
              padding: '12px 16px',
              border: '1px solid #435fe8',
              flexShrink: 0,
              marginTop: '30px',
            }}
            onClick={() => console.log(`🟣 Info box clicked: ${selectedStage}`)}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
              {selectedStage ? (
                <>
                  <Space size={4}>
                    <Text strong>Selected</Text>
                    <div
                      style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colors[selectedStage] }}
                    />
                    <Text strong style={{ color: "rgba(50, 73, 215, 1)" }}>{selectedStage}</Text>
                  </Space>
                  <Text strong>{getStageValue(selectedStage)} <Text type="secondary">candidates</Text></Text>
                </>
              ) : (
                <>
                  <Text strong>Selected: none</Text>
                  <Text strong>0 <Text type="secondary">candidates</Text></Text>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default Stages;
